/**
 * Authored specialist-die roster (HEX-116 / ADR-0018 §3).
 *
 * The die-equivalent of the card pool (`src/combat/cards/`). Pure data against
 * the `DieType` schema in `./dice`. Each specialist die trades the plain d6's
 * flexibility for a GUARANTEED predicate by restricting its face set — the
 * deck-building lever on top of ADR-0015's predicate system: a die that always
 * satisfies a card's cost is powerful for the cards that need it.
 *
 * This is a MINIMAL starter roster proving the mechanic end-to-end; the full
 * roster (more faces / weightings / loaded dice) is deferred content, exactly as
 * the card pool grew from the HEX-50 substrate to the HEX-51 roster.
 *
 * Specialist dice reach a character via level-up offers (HEX-117) and shops
 * (deferred); they are run-scoped — they die with the character (ADR-0018 §3).
 */

import { type DieType, PLAIN_D6 } from './dice';
import type { Card } from './types';

/** Always rolls 1 → guarantees `exact:1` (Brace's costBonus) and `max:N`. */
export const ONES_DIE: DieType = {
  id: 'ones-die',
  name: 'Ones Die',
  faces: [{ face: 1, weight: 1 }],
};

/** Rolls only 2 / 4 / 6 → guarantees `parity:even`. */
export const EVENS_DIE: DieType = {
  id: 'evens-die',
  name: 'Evens Die',
  faces: [
    { face: 2, weight: 1 },
    { face: 4, weight: 1 },
    { face: 6, weight: 1 },
  ],
};

/** Rolls only 4 / 5 / 6 → guarantees `min:4` (Power Strike's cost). */
export const HEAVY_DIE: DieType = {
  id: 'heavy-die',
  name: 'Heavy Die',
  faces: [
    { face: 4, weight: 1 },
    { face: 5, weight: 1 },
    { face: 6, weight: 1 },
  ],
};

/** The specialist (non-default) types — the level-up / shop offer pool. */
export const SPECIALIST_DICE: readonly DieType[] = [ONES_DIE, EVENS_DIE, HEAVY_DIE];

/** Registry of every known die type by id, including the default `PLAIN_D6`.
 * Lets a serialised owned-die be reconciled against the live roster if ever
 * needed; owned dice are stored by value (like cards), so this is a catalogue,
 * not the load path. */
export const DIE_TYPES: Readonly<Record<string, DieType>> = Object.fromEntries(
  [PLAIN_D6, ...SPECIALIST_DICE].map((die) => [die.id, die]),
);

/** Look up a die type by id (catalogue lookup; undefined for unknown ids). */
export function dieById(id: string): DieType | undefined {
  return DIE_TYPES[id];
}

/** Deep clone of a die type — packed dice are stored by value (inside their
 * `die`-card), so cloning keeps a character's dice free of shared references with
 * the roster constants. */
export function cloneDie(die: DieType): DieType {
  return { id: die.id, name: die.name, faces: die.faces.map((fw) => ({ ...fw })) };
}

/** Wrap a `DieType` as a 1×1 `die`-card loadout occupant (ADR-0024 §2). A
 * specialist die gained from a reward becomes one of these and is place-or-declined
 * into the loadout via the ADR-0022 first-fit path (`addCard`), competing with
 * cards for the 9 cells. Empty `cost`/`effects` (the `die` discriminant, ADR-0015
 * §17): it contributes its `dieType` to the roll pool and nothing else. The
 * `dieType` is cloned so the card owns it outright. */
export function dieCard(die: DieType): Card {
  return {
    id: die.id,
    name: die.name,
    type: 'die',
    rarity: 'common',
    size: { w: 1, h: 1 },
    cost: [],
    effects: [],
    dieType: cloneDie(die),
  };
}
